import type { Auth, User } from "firebase/auth";
import { useEffect, useRef, useState } from "react";
import * as api from "./api";
import { AreaSwitchPanel } from "./AreaSwitch";
import {
  accountsText,
  canFinish,
  checkShortText,
  CUTOVER_COLUMNS,
  FINISH_POLL_MS,
  finishedText,
  finishEndedText,
  finishFailedText,
  finishFailure,
  finishQuestion,
  finishReplyState,
  finishRunningText,
  fromSalesAreas,
  nextText,
  ORG_STEP_TEXT,
  readyShareText,
  STEP_TEXT,
  stepCountsText,
  switchDateText,
  watchText,
} from "./cutover";
import { go, useTitle } from "./hooks";
import { dateLabel } from "./format";
import { CheckDetail, Spark } from "./ParityHistory";
import { watchHref } from "./cutoverWatch";
import { CHECK_STATE, CHECK_TEXT, checkChipText, checkState, checkValueText, percentText, streakText } from "./parityHistory";
import { savedSourceText, switchButtonText, switchModes, type SwitchMode } from "./salesAreas";
import { type Sort, sortRows } from "./sort";
import { SortTh, useSort } from "./SortHeader";
import { type Failure, failureOf, Problem } from "./UserPages";
import { Skeleton } from "./Skeleton";

/** An API before roadmap 085 has no /api/cutover: the page then reads the sales areas and the readiness. */
async function load(user: User): Promise<api.Cutover> {
  const token = await user.getIdToken();
  try {
    return await api.cutover(token);
  } catch (e) {
    if (!(e instanceof api.ApiError) || e.status !== 404) throw e;
  }
  const [areas, ready] = await Promise.all([api.salesAreas(token), api.readiness(token).catch(() => null)]);
  console.info(`cutover: an older API, ${areas.areas.length} areas from the sales areas`);
  return fromSalesAreas(areas, ready);
}

/** Design 26.11 F1: the step of the organization and of each area, and the switch of each area. */
export function CutoverPage({ base, org, auth, user, canView }: { base: string; org: api.Bootstrap; auth: Auth; user: User; canView: boolean | undefined }) {
  const [found, setFound] = useState<api.Cutover | Failure | undefined>(undefined);
  const [attempt, setAttempt] = useState(0);
  const [sort, setSort] = useSort("sort", CUTOVER_COLUMNS);
  const [switching, setSwitching] = useState<{ id: string; mode: SwitchMode } | null>(null);
  const [picked, setPicked] = useState<{ area: string; key: api.ReadinessKey } | null>(null);
  const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(null);
  const [finishing, setFinishing] = useState(false);
  const status = useRef<HTMLParagraphElement>(null);
  const table = useRef<HTMLDivElement>(null);
  const polls = useRef(0);
  const wasRunning = useRef(false);
  useTitle(`Cutover · ${org.name}`);
  useEffect(() => {
    if (canView === false) return;
    let live = true;
    const started = performance.now();
    load(user)
      .then((page) => {
        if (!live) return;
        console.info(`cutover: ${page.areas.length} areas, step ${page.header?.step ?? "unknown"} in ${Math.round(performance.now() - started)} ms`);
        setFound(page);
      })
      .catch((e) => live && setFound(failureOf(e)));
    return () => {
      live = false;
    };
  }, [user, attempt, canView]);
  const loaded = typeof found === "object" ? found : null;
  const running = loaded?.finish?.state === "running";
  useEffect(() => {
    if (!running) return;
    const timer = window.setTimeout(() => {
      polls.current += 1;
      console.info(`cutover: the finish runs, poll ${polls.current} after ${FINISH_POLL_MS} ms`);
      setAttempt((n) => n + 1);
    }, FINISH_POLL_MS);
    return () => window.clearTimeout(timer);
  }, [running, found]);
  useEffect(() => {
    if (!loaded) return;
    if (running) {
      wasRunning.current = true;
      return;
    }
    if (!wasRunning.current) return;
    wasRunning.current = false;
    const ended = loaded.finished ? "finished" : (loaded.finish?.state ?? "no state");
    console.info(`cutover: the finish ended (${ended}) after ${polls.current} polls`);
    polls.current = 0;
    const ending = finishEndedText(loaded);
    if (ending) setMessage(ending);
  }, [loaded, running]);
  if (canView === false || found === "hidden") {
    return (
      <main data-testid="cutover-hidden">
        <h1 tabIndex={-1}>Cutover</h1>
        <p>Your role does not see the cutover of the organization.</p>
      </main>
    );
  }
  const page = typeof found === "object" ? found : null;
  const rows = page ? sortRows(page.areas, CUTOVER_COLUMNS, sort) : [];
  const switchArea = page && switching ? page.areas.find((area) => area.id === switching.id) : undefined;
  const pickedArea = page && picked ? page.areas.find((area) => area.code === picked.area) : undefined;
  const pickedCheck = pickedArea?.readiness?.checks.find((check) => check.key === picked?.key);
  const say = (ok: boolean, text: string) => {
    setMessage({ ok, text });
    requestAnimationFrame(() => status.current?.focus());
  };
  const ask = (area: api.CutoverArea, mode: SwitchMode) => {
    setPicked(null);
    setSwitching({ id: area.id, mode });
    setMessage(null);
  };
  const unask = (area: api.CutoverArea) => {
    setSwitching(null);
    requestAnimationFrame(() => table.current?.querySelector<HTMLButtonElement>(`[data-testid=cutover-switch][data-area="${area.code}"]`)?.focus());
  };
  const unpick = (code: string, key: api.ReadinessKey) => {
    setPicked(null);
    requestAnimationFrame(() =>
      table.current?.querySelector<HTMLButtonElement>(`tr[data-area="${code}"] [data-testid=cutover-check][data-check="${key}"] [data-testid=cutover-check-open]`)?.focus(),
    );
  };
  const switched = (area: api.CutoverArea, next: api.SalesAreas, to: api.PickupSource) => {
    setSwitching(null);
    const saved = next.areas.find((row) => row.id === area.id) ?? { ...area, pickup_source: to };
    say(true, savedSourceText(area.code, saved));
    setAttempt((n) => n + 1);
  };
  const finish = async () => {
    if (!window.confirm(finishQuestion(org.name))) return;
    setFinishing(true);
    setMessage(null);
    const started = performance.now();
    try {
      const done = await api.cutoverFinish(await user.getIdToken());
      const ms = Math.round(performance.now() - started);
      if (finishReplyState(done) === "done") {
        console.info(`cutover: finished in the request, ${done.sources?.length ?? 0} sources off, report run ${done.run_state ?? "none"} in ${ms} ms`);
        say(true, "The cutover is finished. Each import source is off.");
      } else {
        console.info(`cutover: the finish job started (${done.id ?? "no id"}) in ${ms} ms`);
        polls.current = 0;
        say(true, "The finish started. This page shows when it ends.");
      }
      setAttempt((n) => n + 1);
    } catch (e) {
      console.warn(`cutover: the finish failed (${e instanceof api.ApiError ? e.status : "no answer"}) in ${Math.round(performance.now() - started)} ms`);
      say(false, finishFailure(e));
    } finally {
      setFinishing(false);
    }
  };
  const download = async () => {
    try {
      const found = await api.cutoverFinishExport(await user.getIdToken());
      console.info("cutover: the export of the finish opens");
      window.location.assign(found.url);
    } catch (e) {
      console.warn(`cutover: the export link failed (${e instanceof api.ApiError ? e.status : "no answer"})`);
      say(false, e instanceof api.ApiError && e.status === 404 ? "The export of the finish is not there. Reload the page." : "The export did not open. Try again in a few minutes.");
    }
  };
  const nextArea = page?.header?.next.area ? page.areas.find((area) => area.code === page.header?.next.area) : undefined;
  const nextMode: SwitchMode | null = page?.header?.next.action === "schedule" ? "schedule" : page?.header?.next.action === "wait_switch" ? "cancel" : null;
  return (
    <main className="cutover-page wide" data-testid="cutover-page" data-state={page ? "loaded" : found === undefined ? "loading" : "failed"}>
      <h1 tabIndex={-1}>Cutover</h1>
      <p className="sub">
        The move of {org.name} from the sheet to the app, one area at a time. An area goes Sheet → Shadow → Ready → Scheduled → On the app → Watch passed.
      </p>
      {found === undefined ? (
        <Skeleton shape="table" columns={6} />
      ) : typeof found === "string" ? (
        <Problem found={found} auth={auth} retry={() => setAttempt((n) => n + 1)} />
      ) : (
        page && (
          <>
            {page.header ? (
              <section className="cutover-header" aria-label="Step of the organization" data-testid="cutover-header" data-step={page.header.step}>
                <p>
                  <strong data-testid="cutover-step">{ORG_STEP_TEXT[page.header.step]}</strong>{" "}
                  <span data-testid="cutover-on-app">
                    {page.header.on_app} of {page.header.areas} {page.header.areas === 1 ? "area" : "areas"} on the app
                  </span>
                  {stepCountsText(page.header.steps) && <span className="sub"> · {stepCountsText(page.header.steps)}</span>}
                </p>
                {page.header.parity.latest && (
                  <p className="cutover-trend">
                    <Spark series={page.header.parity} gate={page.gate.share} label="The organization" width={SPARK_SMALL} />{" "}
                    <span className="sub">Parity of the organization on {dateLabel(page.header.parity.latest.day)}:</span> {percentText(page.header.parity.latest.share)}{" "}
                    <span className="sub">{streakText(page.header.parity.streak, page.gate.cycles)} at the gate</span>
                  </p>
                )}
                <p data-testid="cutover-next" data-action={page.header.next.action}>
                  <span className="sub">Next:</span> {nextText(page.header.next, { accept: page.can_accept, switch: page.areas.some((area) => area.can_switch) })}{" "}
                  {nextArea?.can_switch && nextMode && (
                    <button type="button" className="quiet" data-testid="cutover-next-do" onClick={() => ask(nextArea, nextMode)}>
                      {switchButtonText(nextMode, nextArea.switch_state)}
                    </button>
                  )}
                  {page.header.next.action === "import" && (
                    <a href={`${base}/import`} onClick={go}>
                      Open Admin &gt; Import
                    </a>
                  )}
                  {canFinish(page) && (
                    <button type="button" className="primary" data-testid="cutover-finish" disabled={finishing} onClick={() => void finish()}>
                      {finishing ? "Starting…" : page.finish?.state === "failed" ? "Try the finish again" : "Finish the cutover"}
                    </button>
                  )}
                </p>
                {page.finish?.state === "running" && (
                  <p data-testid="cutover-finish-running" data-at={page.finish.at}>
                    <strong>{finishRunningText(page.finish, org.time_zone)}</strong>
                  </p>
                )}
                {page.finish?.state === "failed" && !page.finished && (
                  <p className="error" data-testid="cutover-finish-failed" data-code={page.finish.code ?? ""}>
                    {finishFailedText(page.finish, canFinish(page))}
                  </p>
                )}
                {page.header.next.action === "finish" && !page.can_finish && !page.finished && (
                  <p className="footnote" data-testid="cutover-finish-who">
                    Only an administrator finishes the cutover.
                  </p>
                )}
                {page.finished && (
                  <p data-testid="cutover-finished" data-at={page.finished.at}>
                    <strong>Finished</strong> <span className="sub">{finishedText(page.finished)}</span>{" "}
                    {page.can_export && page.finished.export && (
                      <button type="button" className="quiet" data-testid="cutover-finished-export" onClick={() => void download()}>
                        Download the export of the finish (CSV)
                      </button>
                    )}
                  </p>
                )}
              </section>
            ) : (
              <p className="hint" data-testid="cutover-old-api">
                The server does not send the step of the organization yet. The rows come from the sales areas.
              </p>
            )}
            {page.can_export && (
              <p className="actions">
                <a href={`${base}/exports`} onClick={go} data-testid="cutover-export">
                  Export the accounts and the pickups (CSV)
                </a>
              </p>
            )}
            {!page.areas.length ? (
              <p data-testid="cutover-empty">No area yet. Add an area on Admin &gt; Sales areas.</p>
            ) : (
              <div className="table-scroll" tabIndex={0} role="region" aria-label="Cutover by area" ref={table}>
                <CutoverTable
                  base={base}
                  page={page}
                  rows={rows}
                  sort={sort}
                  onSort={setSort}
                  picked={picked}
                  switching={switching}
                  onPick={(area, key) => {
                    setSwitching(null);
                    setPicked({ area: area.code, key });
                  }}
                  onSwitch={ask}
                />
              </div>
            )}
            {!page.areas.some((area) => area.can_switch) && page.areas.length > 0 && (
              <p className="footnote">Only the administrator and the operations manager switch an area.</p>
            )}
            {switching && switchArea && (
              <AreaSwitchPanel
                key={`${switching.id}-${switching.mode}`}
                user={user}
                area={switchArea}
                timeZone={org.time_zone}
                mode={switching.mode}
                onSaved={(next, to) => switched(switchArea, next, to)}
                onClose={() => unask(switchArea)}
              />
            )}
            {pickedArea?.readiness && pickedCheck && (
              <CheckDetail
                key={`${pickedArea.code}-${pickedCheck.key}`}
                base={base}
                auth={auth}
                user={user}
                area={{ area: pickedArea.code, accounts: pickedArea.accounts, on_app: pickedArea.switch_state === "app", ...pickedArea.readiness }}
                check={pickedCheck}
                cycles={page.gate.cycles}
                canAccept={page.can_accept}
                heading="h2"
                onClose={() => unpick(pickedArea.code, pickedCheck.key)}
                onSaved={() => setAttempt((n) => n + 1)}
              />
            )}
            <p className="footnote">
              An area is ready when each check passes, or when an administrator accepts the fail with a reason. Pick a check to see the rows that fail.
            </p>
          </>
        )
      )}
      <p ref={status} tabIndex={-1} role="status" data-testid="cutover-status" className={message && !message.ok ? "error" : undefined}>
        {message?.text}
      </p>
    </main>
  );
}

const SPARK_SMALL = 60;
// Rule T-12: the checks are a gate before the switch; after it the watch (T-15) is the measure.
const CHECKED_BEFORE = "Checked before the switch";

/** Rule U-5: the table fits at 1280 px with the menu open. The six checks share one cell of chips. */
export function CutoverTable({
  base,
  page,
  rows,
  sort,
  onSort,
  picked,
  switching,
  onPick,
  onSwitch,
}: {
  base: string;
  page: api.Cutover;
  rows: api.CutoverArea[];
  sort: Sort | null;
  onSort: (next: Sort | null) => void;
  picked: { area: string; key: api.ReadinessKey } | null;
  switching: { id: string; mode: SwitchMode } | null;
  onPick: (area: api.CutoverArea, key: api.ReadinessKey) => void;
  onSwitch: (area: api.CutoverArea, mode: SwitchMode) => void;
}) {
  return (
    <table className="list cutover-table" data-testid="cutover-rows">
      <thead>
        <tr>
          {CUTOVER_COLUMNS.map((column) => (
            <SortTh key={column.key} column={column} sort={sort} onSort={onSort} />
          ))}
          <th scope="col">Actions</th>
          <th scope="col">Checks</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((area) => (
          <tr key={area.id} data-testid="cutover-row" data-area={area.code} data-step={area.step}>
            <th scope="row">
              {area.code}
              <span className="sub"> {accountsText(area.accounts)}</span>
            </th>
            <td data-label="Step" data-testid="cutover-row-step">
              <span className="chip-flag neutral">{STEP_TEXT[area.step]}</span>
            </td>
            <td data-label="Parity" className="wrap">
              {area.parity ? (
                <>
                  <Spark series={area.parity} gate={page.gate.share} label={area.code} width={SPARK_SMALL} /> {percentText(area.parity.latest?.share)}
                  <span className="sub"> {area.parity.meets ? "Gate met" : streakText(area.parity.streak, page.gate.cycles)}</span>
                </>
              ) : (
                "–"
              )}
            </td>
            <td data-label="Ready" className="num" data-testid="cutover-ready">
              {area.switch_state === "app" ? "—" : readyShareText(area)}
            </td>
            <td data-label="Switch date" data-testid="cutover-date">
              {switchDateText(area)}
            </td>
            <td data-label="Watch" className="wrap" data-testid="cutover-row-watch" data-watch={area.watch?.state ?? "none"}>
              {page.can_parity && area.watch && area.watch.state !== "none" ? (
                <a href={watchHref(base, area.id)} onClick={go} data-testid="cutover-row-watch-open" aria-label={`Watch of area ${area.code}: ${watchText(area.watch)}`}>
                  {watchText(area.watch)}
                </a>
              ) : (
                watchText(area.watch)
              )}
            </td>
            <td data-label="Actions" className="wrap">
              {area.can_switch
                ? switchModes(area).map((mode) => (
                    <span key={mode}>
                      <button
                        type="button"
                        className="quiet"
                        data-testid="cutover-switch"
                        data-area={area.code}
                        data-mode={mode}
                        aria-label={`${switchButtonText(mode, area.switch_state).replace("…", "")} of area ${area.code}`}
                        aria-expanded={switching?.id === area.id && switching.mode === mode}
                        onClick={() => onSwitch(area, mode)}
                      >
                        {switchButtonText(mode, area.switch_state)}
                      </button>{" "}
                    </span>
                  ))
                : "–"}
            </td>
            <td data-label="Checks" className="wrap checks">
              {area.switch_state === "app" ? (
                <span className="sub" data-testid="cutover-checks-before">
                  {CHECKED_BEFORE}
                </span>
              ) : area.readiness ? (
                <ul className="cutover-checks">
                  {page.checks.map((key) => {
                    const check = area.readiness?.checks.find((found) => found.key === key);
                    if (!check) return null;
                    const state = CHECK_STATE[checkState(check)];
                    const value = checkShortText(check);
                    return (
                      <li key={key} data-testid="cutover-check" data-check={key} data-ok={check.ok ? "ok" : "fail"}>
                        <button
                          type="button"
                          className={`chip-flag ${state.chip}`}
                          data-testid="cutover-check-open"
                          title={`${CHECK_TEXT[key]}: ${state.text}, ${checkValueText(check, page.gate.cycles)}`}
                          aria-label={`${area.code}, ${CHECK_TEXT[key]}: ${state.text}, ${checkValueText(check, page.gate.cycles)}`}
                          aria-pressed={picked?.area === area.code && picked.key === key}
                          onClick={() => onPick(area, key)}
                        >
                          <span aria-hidden="true">{state.icon}</span> {checkChipText(check)}
                          {value && <span className="value"> {value}</span>}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              ) : (
                "–"
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
